import React, { useState, useEffect, useCallback } from 'react';
import { FiUpload, FiImage, FiSearch, FiTag, FiTrash2, FiEdit2, FiCopy, FiMoreVertical } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import showToast from '../../components/Toast';
import { usePermissions } from '../../contexts/PermissionContext';
import './CDN.css';

const CDN = () => {
  const { hasPermission } = usePermissions();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    tags: '',
    description: '',
    altText: ''
  });
  
  const [editForm, setEditForm] = useState({
    tags: '',
    description: '',
    altText: ''
  });

  const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

  // Fetch images
  const fetchImages = useCallback(async (page = 1, tags = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (tags) {
        params.append('tags', tags);
      }

      const response = await AxiosInstance.get(`${BASE_URL}/cdn/my-images?${params}`);
      
      if (response.data?.statusCode === 200) {
        setImages(response.data.data.images);
        setTotalPages(response.data.data.pagination.pages);
        setCurrentPage(response.data.data.pagination.page);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      showToast.error('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // File validation
  const validateFile = (file) => {
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('File size must be less than 10MB');
      return false;
    }
    if (!file.type.startsWith('image/')) {
      showToast.error('Please select an image file');
      return false;
    }
    return true;
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    }
  };

  // Handle new image file selection for edit
  const handleNewImageSelect = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      setNewImageFile(file);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      showToast.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('tags', uploadForm.tags);
      formData.append('description', uploadForm.description);
      formData.append('altText', uploadForm.altText);

      const response = await AxiosInstance.post(`${BASE_URL}/cdn/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data?.statusCode === 201) {
        showToast.success('Image uploaded successfully!');
        setShowUploadModal(false);
        setSelectedFile(null);
        setUploadForm({ tags: '', description: '', altText: '' });
        fetchImages();
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    const tags = selectedTags.join(',');
    fetchImages(1, tags);
  };

  // Handle delete
  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const response = await AxiosInstance.delete(`${BASE_URL}/cdn/image/${imageId}`);
      if (response.data?.statusCode === 200) {
        showToast.success('Image deleted successfully');
        fetchImages(currentPage, selectedTags.join(','));
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      showToast.error('Failed to delete image');
    }
  };

  // Handle edit
  const handleEdit = async () => {
    if (!editingImage) return;

    try {
      setUploading(true);
      const formData = new FormData();
      
      formData.append('tags', editForm.tags || '');
      formData.append('description', editForm.description || '');
      formData.append('altText', editForm.altText || '');

      if (newImageFile) {
        formData.append('image', newImageFile);
      }

      const response = await AxiosInstance.put(`${BASE_URL}/cdn/image/${editingImage._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data?.statusCode === 200) {
        showToast.success('Image updated successfully!');
        setShowEditModal(false);
        setEditingImage(null);
        setEditForm({ tags: '', description: '', altText: '' });
        setNewImageFile(null);
        fetchImages(currentPage, selectedTags.join(','));
      }
    } catch (error) {
      console.error('Error updating image:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update image';
      showToast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Open edit modal
  const openEditModal = (image) => {
    setEditingImage(image);
    setEditForm({
      tags: image.tags && Array.isArray(image.tags) ? image.tags.join(', ') : (image.tags || ''),
      description: image.description || '',
      altText: image.altText || ''
    });
    setNewImageFile(null);
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  // Copy image URL
  const copyImageUrl = (url) => {
    const fullUrl = `${config.api.baseURL}${url}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      showToast.success('Image URL copied to clipboard!');
    });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle pagination
  const handlePageChange = (page) => {
    const tags = selectedTags.join(',');
    fetchImages(page, tags);
  };

  // Handle menu toggle
  const toggleMenu = (imageId) => {
    setOpenMenuId(openMenuId === imageId ? null : imageId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.image-menu')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Reset form handlers
  const resetUploadForm = () => {
    setUploadForm({ tags: '', description: '', altText: '' });
    setSelectedFile(null);
  };

  const resetEditForm = () => {
    setEditForm({ tags: '', description: '', altText: '' });
    setNewImageFile(null);
    setEditingImage(null);
  };

  if (loading) {
    return (
      <div className="cdn-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cdn-page">
      <div className="page-header">
        <div className="header-left-cdn">
          <FiImage className="page-icon" />
          <div>
            <h1>Image CDN</h1>
            <p>Upload and manage your images</p>
          </div>
        </div>
        {hasPermission('canUploadImageCDN') && (
          <button 
            className="upload-btn"
            onClick={() => setShowUploadModal(true)}
          >
            <FiUpload />
            Upload Image
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="search-section">
        <div className="search-bar">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by tags..."
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>Search</button>
        </div>
        <div className="tag-filters">
          <FiTag />
          <span>Popular tags:</span>
          {['logo', 'banner', 'product', 'team', 'background'].map(tag => (
            <button
              key={tag}
              className={`tag-btn ${selectedTags.includes(tag) ? 'active' : ''}`}
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  setSelectedTags(selectedTags.filter(t => t !== tag));
                } else {
                  setSelectedTags([...selectedTags, tag]);
                }
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Images Grid */}
      <div className="images-section">
        {images.length === 0 ? (
          <div className="empty-state">
            <FiImage className="empty-icon" />
            <h3>No images found</h3>
            <p>Upload your first image to get started</p>
            {hasPermission('canUploadImageCDN') && (
              <button 
                className="upload-btn"
                onClick={() => setShowUploadModal(true)}
              >
                <FiUpload />
                Upload Image
              </button>
            )}
          </div>
        ) : (
          <div className="images-grid">
            {images.map(image => (
              <div key={image._id} className="image-card">
                <div className="image-preview">
                  <img 
                    src={`${config.api.baseURL}${image.url}`} 
                    alt={image.altText || image.originalName}
                    loading="lazy"
                  />
                  <div className="image-overlay">
                    <div className="image-menu-container">
                      <button 
                        className="menu-trigger"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(image._id);
                        }}
                        title="More options"
                      >
                        <FiMoreVertical />
                      </button>
                      {openMenuId === image._id && (
                        <div className="image-menu">
                          <button 
                            className="menu-item"
                            onClick={() => {
                              copyImageUrl(image.url);
                              setOpenMenuId(null);
                            }}
                          >
                            <FiCopy />
                            Copy URL
                          </button>
                          {hasPermission('canEditImageCDN') && (
                            <button 
                              className="menu-item"
                              onClick={() => openEditModal(image)}
                            >
                              <FiEdit2 />
                              Edit
                            </button>
                          )}
                          {hasPermission('canDeleteImageCDN') && (
                            <button 
                              className="menu-item delete"
                              onClick={() => {
                                handleDelete(image._id);
                                setOpenMenuId(null);
                              }}
                            >
                              <FiTrash2 />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="image-info">
                  <h4>{image.originalName}</h4>
                  <p className="image-meta">
                    {formatFileSize(image.size)} • {image.width}x{image.height}
                  </p>
                  {image.tags && image.tags.length > 0 && (
                    <div className="image-tags">
                      {image.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                      {image.tags.length > 3 && (
                        <span className="tag more">+{image.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                  <p className="upload-date">
                    {new Date(image.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Upload Image</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { 
                e.preventDefault(); 
                if (selectedFile && !uploading) {
                  handleUpload();
                }
              }}>
                <div className="file-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    id="file-input"
                  />
                  <label htmlFor="file-input">
                    <FiUpload />
                    {selectedFile ? selectedFile.name : 'Choose an image file'}
                  </label>
                </div>
                
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input
                    type="text"
                    placeholder="e.g., logo, banner, product"
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Optional description"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Alt Text</label>
                  <input
                    type="text"
                    placeholder="Alternative text for accessibility"
                    value={uploadForm.altText}
                    onChange={(e) => setUploadForm({...uploadForm, altText: e.target.value})}
                  />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                }}
              >
                Cancel
              </button>
              <button 
                className="upload-btn"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingImage && (
        <div className="modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Edit Image</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowEditModal(false);
                  resetEditForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-image-preview">
                <img 
                  src={`${config.api.baseURL}${editingImage.url}`} 
                  alt={editingImage.altText || editingImage.originalName}
                />
                <p className="image-filename">{editingImage.originalName}</p>
              </div>
              
              <form onSubmit={(e) => { 
                e.preventDefault(); 
                if (!uploading) {
                  handleEdit();
                }
              }}>
                <div className="form-group">
                  <label>Replace Image (Optional)</label>
                  <div className="file-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleNewImageSelect}
                      id="edit-file-input"
                    />
                    <label htmlFor="edit-file-input">
                      <FiUpload />
                      {newImageFile ? newImageFile.name : 'Choose a new image file'}
                    </label>
                  </div>
                  {newImageFile && (
                    <p className="file-info">
                      New file: {newImageFile.name} ({(newImageFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input
                    type="text"
                    placeholder="e.g., logo, banner, product"
                    value={editForm.tags}
                    onChange={(e) => setEditForm({...editForm, tags: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Optional description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Alt Text</label>
                  <input
                    type="text"
                    placeholder="Alternative text for accessibility"
                    value={editForm.altText}
                    onChange={(e) => setEditForm({...editForm, altText: e.target.value})}
                  />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowEditModal(false);
                  resetEditForm();
                }}
              >
                Cancel
              </button>
              <button 
                className="upload-btn"
                onClick={handleEdit}
                disabled={uploading}
              >
                {uploading ? 'Updating...' : 'Update Image'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CDN;
